import { usePartnerSelector } from '../../store';
import { selectLocalCartCount } from '../../store/localCartSlice';

interface CartIconButtonProps {
  onClick: () => void;
  isActive?: boolean;
}

/**
 * CartIconButton
 *
 * Persistent cart icon shown in the embed tab header.
 * Displays a badge with the number of items in the local cart.
 * Reads count from Redux localCartSlice — independent of CartContext.
 */
export function CartIconButton({
  onClick,
  isActive = false
}: CartIconButtonProps) {
  const count = usePartnerSelector(selectLocalCartCount);

  return (
    <button
      onClick={onClick}
      aria-label={
        count > 0 ? `Cart — ${count} item${count === 1 ? '' : 's'}` : 'Cart'
      }
      className={[
        'relative flex w-11 shrink-0 cursor-pointer items-center justify-center border-0 border-b-2 bg-transparent transition-colors',
        isActive
          ? 'text-accent border-b-accent'
          : 'text-muted-text border-b-transparent'
      ].join(' ')}
    >
      {/* Cart SVG — matches CartButton.tsx */}
      <svg
        className="h-5 w-5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>

      {/* Item count badge — only shown when count > 0 */}
      {count > 0 && (
        <span className="bg-accent text-accent-contrast absolute right-[4px] top-[4px] flex h-[14px] w-[14px] items-center justify-center rounded-full text-[9px] leading-none">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
}
